import { GetterTree, MutationTree, ActionTree, ActionContext, Module } from 'vuex';
import moment from 'moment';
import { RootState } from '../types';
import { AccelerometerStatus } from '@/api/ux_logs';
import { sensorStart, sensorStop, getIsSensor, getSensorAcc, getSensorAig, getSensorRot } from '@/util/sensorMotion';

declare global {
  interface Window {
    DeviceMotionEvent: {
      requestPermission: () => Promise<'granted' | 'denied'>;
    };
  }
}

export type SensorData = [
  string,
  string,
  [number, number, number],
  [number, number, number],
  [number, number, number],
  number,
  number
];

type State = {
  isSetup: boolean;
  startCount: number;
  triggerCountShoot: number;
  triggerCountConfirmStart: number;
  triggerCountConfirmButton: number;
  stack: SensorData[];
  status: AccelerometerStatus;
};

function createState(): State {
  const state: State = {} as State;
  clearState(state);
  return state;
}

function clearState(state: State): void {
  state.isSetup = false;
  state.startCount = 0;
  state.triggerCountShoot = 0;
  state.triggerCountConfirmStart = 0;
  state.triggerCountConfirmButton = 0;
  state.stack = [];
  state.status = AccelerometerStatus.UNAVAILABLE;
}

const getters: GetterTree<State, RootState> = {
  stack: (state: State): SensorData[] => state.stack.slice(-1500),
  status: (state: State): AccelerometerStatus => state.status
};

const mutations: MutationTree<State> = {
  setSetup(state: State): void {
    state.isSetup = true;
  },

  setStartCount(state: State, payload: number): void {
    state.startCount = payload;
  },

  addTriggerCountShoot(state: State): void {
    state.triggerCountShoot++;
  },

  addTriggerCountConfirmStart(state: State): void {
    state.triggerCountConfirmStart++;
  },

  addTriggerCountConfirmButton(state: State): void {
    state.triggerCountConfirmButton++;
  },

  pushSensorData(state: State, payload: SensorData) {
    state.stack.push(payload);
  },

  clearSensorData(state: State) {
    state.stack = [];
  },

  setStatus(state: State, payload: AccelerometerStatus): void {
    state.status = payload;
  },

  clearState(state: State) {
    clearState(state);
  }
};

const actions: ActionTree<State, RootState> = {
  async setup(context: ActionContext<State, RootState>): Promise<void> {
    if (window.DeviceMotionEvent && window.DeviceMotionEvent.requestPermission) {
      const status: 'granted' | 'denied' = await window.DeviceMotionEvent.requestPermission();
      if (status !== 'granted') context.commit('setStatus', AccelerometerStatus.DENIED);
    }

    context.commit('setSetup');

    // setup前にstartしていたらここでsensorStartを行う
    if (context.state.startCount > 0) sensorStart();
  },

  start(context: ActionContext<State, RootState>): void {
    if (context.state.startCount < 0) return;
    context.commit('setStartCount', context.state.startCount + 1);
    if (!context.state.isSetup) return;
    if (context.state.startCount > 1) return;
    sensorStart();
  },

  stop(context: ActionContext<State, RootState>): void {
    if (context.state.startCount <= 0) return;
    context.commit('setStartCount', context.state.startCount - 1);
    if (!context.state.isSetup) return;
    if (context.state.startCount > 0) return;
    sensorStop();
  },

  push(context: ActionContext<State, RootState>, payload: { hash: string; event: number; status: number }): void {
    const sensorData: SensorData = [
      moment.utc().tz('Asia/Tokyo').format('YYYYMMDD:HHmmss.SSS'),
      payload.hash,
      getSensorAcc(),
      getSensorAig(),
      getSensorRot(),
      payload.event,
      payload.status
    ];

    if (getIsSensor()) context.commit('setStatus', AccelerometerStatus.GRANTED);
    context.commit('pushSensorData', sensorData);
  },

  clearState(context: ActionContext<State, RootState>): void {
    context.commit('clearState');
  }
};

export const sensorMotion: Module<State, RootState> = {
  namespaced: true,
  state: createState,
  getters,
  mutations,
  actions
};
